import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../Services/supabaseClient";
import { useAuth } from "../../Services/Context/AuthContext";
import { useDarkMode } from "../../Services/Context/DarkModeContext";
import "./AuthModal.css";

type View = "options" | "email_sent" | "terms" | "privacy";

interface AuthModalProps {
  onClose: () => void;
}

const AuthModal = ({ onClose }: AuthModalProps) => {
  const { user } = useAuth();
  const { dark } = useDarkMode();
  const [email, setEmail] = useState("");
  const [view, setView] = useState<View>("options");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) onClose();
  }, [user, onClose]);

  const handleGoogle = async () => {
    if (!supabase) {
      setError("Auth not configured.");
      return;
    }
    setIsLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: process.env.REACT_APP_SITE_URL ?? window.location.origin },
    });
    if (error) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError("Auth not configured.");
      return;
    }
    if (!email.trim()) return;
    setIsLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setIsLoading(false);
    if (error) {
      setError("Couldn't send the sign-in email. Please try again in a few minutes, or use Google sign-in.");
    } else {
      setView("email_sent");
    }
  };

  const isLegalView = view === "terms" || view === "privacy";

  return (
    <motion.div
      className="AuthModal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className={`AuthModal-card ${dark ? "AuthModal-card--dark" : ""} ${isLegalView ? "AuthModal-card--legal" : ""}`}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        {isLegalView ? (
          <>
            <div className="AuthModal-legal-header">
              <button
                className="AuthModal-back-btn"
                onClick={() => setView("options")}
                aria-label="Back"
              >
                ← Back
              </button>
              <h2 className="AuthModal-legal-title">
                {view === "terms" ? "Terms of Service" : "Privacy Policy"}
              </h2>
            </div>
            <div className="AuthModal-legal-body">
              <AnimatePresence mode="wait">
                {view === "terms" ? (
                  <TermsContent key="terms" />
                ) : (
                  <PrivacyContent key="privacy" />
                )}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <>
            <button
              className="AuthModal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="AuthModal-sigil">✦</div>
            <h2 className="AuthModal-title">Welcome to FateSpin</h2>
            <p className="AuthModal-subtitle">
              Sign in to access more features
            </p>

            {view === "options" ? (
              <>
                <button
                  className="AuthModal-google"
                  onClick={handleGoogle}
                  disabled={isLoading}
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <div className="AuthModal-divider">
                  <span>or</span>
                </div>

                <form
                  onSubmit={handleMagicLink}
                  className="AuthModal-email-form"
                >
                  <input
                    type="email"
                    className={`AuthModal-email-input ${dark ? "AuthModal-email-input--dark" : ""}`}
                    placeholder="Please enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="submit"
                    className="AuthModal-magic-btn"
                    disabled={isLoading || !email.trim()}
                  >
                    {isLoading ? "Sending…" : "Send Magic Link"}
                  </button>
                </form>

                {error && <p className="AuthModal-error">{typeof error === "string" ? error : "Something went wrong. Please try again."}</p>}
              </>
            ) : (
              <div className="AuthModal-sent">
                <div className="AuthModal-sent-icon">✉</div>
                <p className="AuthModal-sent-text">
                  Check your email — we sent a sign-in link to{" "}
                  <strong>{email}</strong>.
                </p>
                <button
                  className="AuthModal-back"
                  onClick={() => {
                    setView("options");
                    setError(null);
                  }}
                >
                  Use a different email
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export const TermsContent = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <p className="AuthModal-legal-date">Last updated: June 2026</p>

    <h3>1. Entertainment Purposes Only</h3>
    <p>
      FateSpin provides tarot readings for entertainment and personal reflection
      only. Nothing on this platform constitutes professional advice of any kind
      — including medical, psychological, legal, financial, or any other
      regulated field. Do not make important life decisions based solely on a
      reading. If you are in crisis, please contact a qualified professional or
      emergency service.
    </p>

    <h3>2. Eligibility</h3>
    <p>
      You must be at least 13 years old to create an account. By signing up you
      confirm you meet this requirement. If you are under 18, you should have
      parental consent before using this service.
    </p>

    <h3>3. Your Account</h3>
    <p>
      You are responsible for maintaining the security of your account. We use
      email magic links and Google OAuth — we never store passwords. You may
      delete your account at any time by contacting us.
    </p>

    <h3>4. Acceptable Use</h3>
    <p>
      You agree not to misuse FateSpin — including attempting to
      reverse-engineer the service, overload our systems, or submit content that
      is harmful, illegal, or violates the rights of others.
    </p>

    <h3>5. AI-Generated Readings</h3>
    <p>
      Readings are generated by an AI language model (Anthropic Claude). AI
      outputs can be inaccurate, inconsistent, or unexpected. We make no
      guarantees about the accuracy or meaning of any reading.
    </p>

    <h3>6. Changes to the Service</h3>
    <p>
      We may modify, suspend, or discontinue any part of FateSpin at any time.
      We may also update these Terms — continued use of the service after
      changes constitutes acceptance of the updated Terms.
    </p>

    <h3>7. Disclaimer of Warranties</h3>
    <p>
      FateSpin is provided "as is" without warranties of any kind. We do not
      guarantee uninterrupted or error-free operation.
    </p>

    <h3>8. Limitation of Liability</h3>
    <p>
      To the fullest extent permitted by law, FateSpin and its creators are not
      liable for any indirect, incidental, or consequential damages arising from
      your use of the service.
    </p>

    <h3>9. Contact</h3>
    <p>
      Questions about these Terms? Email us at{" "}
      <a href="mailto:FateSpinTarot@gmail.com">FateSpinTarot@gmail.com</a>.
    </p>
  </motion.div>
);

export const PrivacyContent = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <p className="AuthModal-legal-date">Last updated: June 2026</p>

    <h3>1. What We Collect</h3>
    <p>
      When you create an account we collect your <strong>email address</strong>{" "}
      solely for authentication. When you do a reading we log: the card drawn,
      whether it was reversed, keywords extracted from your question (not the
      full question text), your IP address, approximate location (country/city),
      browser and device type, and your user ID if signed in.
    </p>

    <h3>2. What We Do Not Collect</h3>
    <p>
      We do not store the full text of your questions in our database — only
      keywords. We do not collect payment information (no payments are currently
      accepted). We do not sell your data.
    </p>

    <h3>3. How We Use Your Data</h3>
    <p>
      Your data is used to: authenticate your account and enforce fair usage
      rate limits, and improve the service. We do not use your data for
      advertising.
    </p>

    <h3>4. Third-Party Services</h3>
    <p>
      <strong>Supabase</strong> — handles authentication and stores your reading
      history. Data is stored in their secure cloud database.{" "}
      <strong>Anthropic</strong> — your question text is sent to Anthropic's
      Claude API to generate the reading. Anthropic's privacy policy governs
      that data. <strong>Google</strong> — if you sign in with Google, Google's
      authentication service is used. <strong>Vercel</strong> — hosts the
      application and collects anonymous analytics (page views, performance
      data).
    </p>

    <h3>5. Data Retention</h3>
    <p>
      Reading records are retained for up to 12 months, after which they are
      deleted.
    </p>

    <h3>6. Your Rights</h3>
    <p>
      You may request access to, correction of, or deletion of your personal
      data at any time. To do so, email us at{" "}
      <a href="mailto:FateSpinTarot@gmail.com">FateSpinTarot@gmail.com</a>. If
      you are in the EU or UK you have additional rights under GDPR.
    </p>

    <h3>7. Cookies</h3>
    <p>
      We use browser local storage to remember your session and preferences
      (such as dark mode and language). We do not use third-party tracking
      cookies.
    </p>

    <h3>8. Changes to This Policy</h3>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of
      significant changes by updating the date at the top of this page.
    </p>

    <h3>9. Contact</h3>
    <p>
      Privacy questions? Email us at{" "}
      <a href="mailto:FateSpinTarot@gmail.com">FateSpinTarot@gmail.com</a>.
    </p>
  </motion.div>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

export default AuthModal;
